import { useEffect, useState } from "react";
import { Facebook, Instagram, Youtube, Music2, ExternalLink, ArrowUpRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { PageHero, Loading } from "@/components/shared";

const ICONS = { facebook: Facebook, instagram: Instagram, youtube: Youtube, tiktok: Music2 };
const BRAND = { facebook: "#1877F2", instagram: "#E4405F", youtube: "#FF0000", tiktok: "#0B2A4A" };

export default function SocialMedia() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/public/page/social_media").then((r) => setC(r.data?.content || {})); }, []);

  if (c === null) return <Loading />;
  const platforms = (c.platforms || []).filter((p) => p.enabled && p.url);
  const future = (c.platforms || []).filter((p) => !p.enabled);
  const posts = c.featured_posts || [];

  return (
    <>
      <SEO title="Social Media | Brackenfell Junior Rugby Club" description="Follow Brackenfell Junior Rugby Club on Facebook and Instagram for training, match highlights and rugby moments." />
      <PageHero title={c.heading || "Follow The BJRC Action"} subtitle={c.description} />

      {/* PLATFORM LINKS */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-5 sm:grid-cols-2">
          {platforms.map((p) => {
            const Icon = ICONS[p.key] || ExternalLink;
            return (
              <a key={p.key} href={p.url} target="_blank" rel="noreferrer noopener" data-testid={`social-link-${p.key}`}
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl text-white" style={{ backgroundColor: BRAND[p.key] || "#0B2A4A" }}>
                  <Icon className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <div className="font-heading text-lg font-bold uppercase text-[#0B2A4A]">{p.label}</div>
                  <div className="text-sm text-slate-500">Follow us on {p.label}</div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-slate-400 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
              </a>
            );
          })}
        </div>
      </section>

      {/* FEATURED HIGHLIGHTS */}
      {posts.length > 0 && (
        <section className="bg-white py-14 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <span className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#1E63D6]">Recent Highlights</span>
              <h2 className="mt-2 font-heading text-3xl font-extrabold uppercase tracking-tight text-[#0B2A4A] sm:text-4xl">From Our Socials</h2>
            </div>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post, i) => {
                const Icon = ICONS[post.platform] || ExternalLink;
                return (
                  <a key={i} href={post.url || "#"} target="_blank" rel="noreferrer noopener" data-testid={`social-post-${i}`}
                    className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
                    <div className="relative h-60 bg-[#0B2A4A]">
                      {post.image && <img src={mediaUrl(post.image)} alt={post.caption || "BJRC highlight"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                      <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ backgroundColor: BRAND[post.platform] || "#0B2A4A" }}>
                        <Icon className="h-4.5 w-4.5" />
                      </div>
                    </div>
                    {post.caption && <div className="p-4"><p className="text-sm text-slate-700">{post.caption}</p></div>}
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FUTURE PLATFORMS */}
      {future.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
            <p className="text-sm font-bold uppercase tracking-wide text-slate-500">Coming soon</p>
            <p className="mt-2 text-slate-600">We'll be adding {future.map((f) => f.label).join(" and ")} soon — stay tuned!</p>
          </div>
        </section>
      )}
    </>
  );
}
